import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

function Receipt() {
  const { saleId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const autoPrint = searchParams.get("autoPrint") === "true";

  const [sale, setSale] = useState(null);
  const [shop, setShop] = useState(null);
  const [message, setMessage] = useState("");
  const [isVoiding, setIsVoiding] = useState(false);
  const [refundQty, setRefundQty] = useState({});
  const [refundReason, setRefundReason] = useState("");
  const [isRefunding, setIsRefunding] = useState(false);

  const token = localStorage.getItem("token")?.trim();

  const authHeaders = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const fetchSale = async () => {
    try {
      const res = await axios.get(`${API_URL}/sales/${saleId}`, authHeaders);
      setSale(res.data);
    } catch (error) {
      setMessage(error.response?.data?.message || "Could not load receipt.");
    }
  };

  const fetchShop = async () => {
    try {
      const res = await axios.get(`${API_URL}/shop`, authHeaders);
      setShop(res.data);
    } catch (error) {
      setShop({
        shopName: "African Grocery Store",
        address: "",
        phone: "",
        website: "",
        logoUrl: "",
      });
    }
  };

  useEffect(() => {
    fetchSale();
    fetchShop();
  }, [saleId]);

  // Auto print only when opened from checkout with ?autoPrint=true
  useEffect(() => {
    if (!sale || !autoPrint) return;

    const printTimer = setTimeout(() => {
      window.print();
    }, 500);

    const returnTimer = setTimeout(() => {
      navigate("/");
    }, 2500);

    return () => {
      clearTimeout(printTimer);
      clearTimeout(returnTimer);
    };
  }, [sale, autoPrint, navigate]);

  const printReceipt = () => {
    window.print();
  };

  const voidSale = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to void this full sale? Stock will be restored."
    );

    if (!confirmed) return;

    try {
      setIsVoiding(true);

      await axios.put(
        `${API_URL}/sales/${sale._id}/void`,
        { reason: "Voided from receipt page" },
        authHeaders
      );

      setMessage("Sale voided successfully. Stock has been restored.");
      await fetchSale();
    } catch (error) {
      alert(error.response?.data?.message || "Void failed.");
    } finally {
      setIsVoiding(false);
    }
  };

  const refundItem = async (productId) => {
    const quantity = Number(refundQty[productId]);

    if (!quantity || quantity <= 0) {
      alert("Enter a valid refund quantity.");
      return;
    }

    try {
      setIsRefunding(true);

      await axios.post(
        `${API_URL}/sales/${sale._id}/refund`,
        {
          reason: refundReason || "No reason provided",
          items: [{ productId, quantity }],
        },
        authHeaders
      );

      setMessage("Item refunded successfully. Stock has been restored.");
      setRefundQty({});
      setRefundReason("");

      await fetchSale();
    } catch (error) {
      alert(error.response?.data?.message || "Refund failed.");
    } finally {
      setIsRefunding(false);
    }
  };

  if (!sale && message) {
    return <p style={{ padding: "20px" }}>{message}</p>;
  }

  if (!sale) {
    return <p style={{ padding: "20px" }}>Loading receipt...</p>;
  }

  return (
    <div style={styles.page}>
      {message && <p style={styles.message}>{message}</p>}

      <div style={styles.receipt} className="receipt-print-area">
        <div style={styles.center}>
          {shop?.logoUrl && (
            <img src={shop.logoUrl} alt={shop.shopName} style={styles.logo} />
          )}

          <h2 style={styles.storeName}>
            {shop?.shopName || "African Grocery Store"}
          </h2>

          {shop?.address && <p>{shop.address}</p>}
          {shop?.phone && <p>Tel: {shop.phone}</p>}
          {shop?.website && <p>{shop.website}</p>}
        </div>

        <hr />

        <p>
          <strong>Receipt:</strong> {sale.receiptNumber}
        </p>
        <p>
          <strong>Date:</strong> {new Date(sale.createdAt).toLocaleString()}
        </p>
        <p>
          <strong>Cashier:</strong> {sale.cashier?.name || "Unknown"}
        </p>
        <p>
          <strong>Terminal:</strong>{" "}
          {sale.terminal?.terminalName || "Unknown Terminal"}
        </p>
        <p>
          <strong>Terminal ID:</strong>{" "}
          {sale.terminal?.terminalId || "UNKNOWN_TERMINAL"}
        </p>
        <p>
          <strong>Payment:</strong> {sale.paymentMethod}
        </p>
        <p>
          <strong>Status:</strong>{" "}
          <span style={sale.status === "voided" ? styles.voidedText : {}}>
            {sale.status}
          </span>
        </p>

        {sale.refundStatus && sale.refundStatus !== "none" && (
          <p>
            <strong>Refund status:</strong> {sale.refundStatus} — £
            {Number(sale.totalRefunded || 0).toFixed(2)} refunded
          </p>
        )}

        {sale.status === "voided" && (
          <>
            <p>
              <strong>Voided by:</strong> {sale.voidedBy?.name || "Unknown"}
            </p>
            <p>
              <strong>Reason:</strong> {sale.voidReason || "No reason provided"}
            </p>
          </>
        )}

        <hr />

        {sale.items.map((item, index) => (
          <div key={index} style={styles.item}>
            <div>
              <strong>{item.name}</strong>
              <p>
                {item.quantity} {item.unitLabel} × £
                {Number(item.sellingPrice).toFixed(2)}
              </p>

              {sale.status === "completed" && (
                <div style={styles.refundBox}>
                  <input
                    type="number"
                    step={item.saleType === "weight" ? "0.01" : "1"}
                    min="0"
                    placeholder="Qty"
                    value={refundQty[item.productId] || ""}
                    onChange={(e) =>
                      setRefundQty((prev) => ({
                        ...prev,
                        [item.productId]: e.target.value,
                      }))
                    }
                    style={styles.refundInput}
                  />

                  <button
                    onClick={() => refundItem(item.productId)}
                    disabled={isRefunding}
                    style={styles.refundButton}
                  >
                    {isRefunding ? "..." : "Refund"}
                  </button>
                </div>
              )}
            </div>

            <strong>£{Number(item.lineTotal).toFixed(2)}</strong>
          </div>
        ))}

        {sale.status === "completed" && (
          <textarea
            placeholder="Refund reason (optional)"
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            style={styles.textarea}
          />
        )}

        <hr />

        <div style={styles.totalRow}>
          <strong>Total</strong>
          <strong>£{Number(sale.totalAmount).toFixed(2)}</strong>
        </div>

        {sale.totalRefunded > 0 && (
          <>
            <div style={styles.totalRowSmall}>
              <span>Refunded</span>
              <span>-£{Number(sale.totalRefunded || 0).toFixed(2)}</span>
            </div>

            <div style={styles.totalRowSmall}>
              <strong>Net Total</strong>
              <strong>
                £
                {Number(
                  sale.totalAmount - (sale.totalRefunded || 0)
                ).toFixed(2)}
              </strong>
            </div>
          </>
        )}

        {sale.status === "voided" && <div style={styles.voidStamp}>VOIDED</div>}

        <div style={styles.center}>
          <p style={styles.footerText}>
            {shop?.receiptFooter || "Thank you for shopping with us."}
          </p>
        </div>
      </div>

      <div style={styles.actionRow}>
        <button onClick={printReceipt} style={styles.printButton}>
          Print Receipt
        </button>

        <button onClick={() => navigate("/")} style={styles.backButton}>
          New Sale
        </button>
      </div>

      {sale.status === "completed" && (
        <button
          onClick={voidSale}
          disabled={isVoiding}
          style={styles.voidButton}
        >
          {isVoiding ? "Voiding..." : "Void Full Sale"}
        </button>
      )}
     <style>
  {`
    @media print {

      @page {
        size: 80mm auto;
        margin: 0;
      }

      html,
      body {
        width: 80mm;
        margin: 0 !important;
        padding: 0 !important;
        background: white !important;
      }

      body * {
        visibility: hidden !important;
      }

      .receipt-print-area,
      .receipt-print-area * {
        visibility: visible !important;
      }

      .receipt-print-area {
        position: absolute !important;
        left: 0 !important;
        top: 0 !important;

        width: 72mm !important;
        max-width: 72mm !important;

        margin: 0 !important;
        padding: 4mm !important;

        background: white !important;

        box-shadow: none !important;
        border-radius: 0 !important;

        font-family: Arial, sans-serif !important;
        font-size: 11px !important;
        line-height: 1.2 !important;
      }

      .receipt-print-area h2 {
        font-size: 15px !important;
        margin: 0 0 3px 0 !important;
      }

      .receipt-print-area p,
      .receipt-print-area div,
      .receipt-print-area span,
      .receipt-print-area strong {
        font-size: 11px !important;
        line-height: 1.2 !important;
        margin: 1px 0 !important;
      }

      .receipt-print-area hr {
        margin: 5px 0 !important;
      }

      button,
      textarea,
      input {
        display: none !important;
      }
    }
  `}
</style>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f4f6f8",
    padding: "30px",
    fontFamily: "Arial, sans-serif",
  },
  message: {
    maxWidth: "420px",
    margin: "0 auto 16px",
    padding: "12px",
    background: "#fff3cd",
    borderRadius: "8px",
    fontSize: "14px",
  },
  receipt: {
    width: "420px",
    margin: "0 auto",
    background: "white",
    padding: "14px",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    fontSize: "16px",
  },
  center: {
    textAlign: "center",
  },
  logo: {
    maxWidth: "70px",
    maxHeight: "70px",
    objectFit: "contain",
    marginBottom: "8px",
  },
  storeName: {
    margin: "0 0 6px",
  },
  item: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "4px",
    padding: "6px 0",

  },
  refundBox: {
    marginTop: "6px",
    display: "flex",
    gap: "6px",
  },
  refundInput: {
    width: "60px",
    padding: "6px",
    borderRadius: "6px",
    border: "1px solid #ccc",
  },
  refundButton: {
    padding: "6px 10px",
    borderRadius: "6px",
    border: "none",
    background: "#faad14",
    color: "white",
    cursor: "pointer",
  },
  textarea: {
    width: "100%",
    marginTop: "10px",
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "18px",
    marginTop: "4px",
  },
  totalRowSmall: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "13px",
    marginTop: "8px",
  },
  actionRow: {
    display: "flex",
    justifyContent: "center",
    gap: "12px",
    marginTop: "20px",
  },
  printButton: {
    padding: "14px 22px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
  },
  backButton: {
    padding: "14px 22px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    background: "white",
  },
  voidButton: {
    display: "block",
    margin: "10px auto",
    padding: "14px 22px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    background: "#ff4d4f",
    color: "white",
  },
  voidedText: {
    color: "#ff4d4f",
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  voidStamp: {
    margin: "16px 0",
    padding: "10px",
    textAlign: "center",
    border: "2px solid #ff4d4f",
    color: "#ff4d4f",
    fontWeight: "bold",
    fontSize: "22px",
    letterSpacing: "2px",
  },
  footerText: {
    marginTop: "4px",
    whiteSpace: "pre-line",
    fontSize: "13px",
    color: "#444",
  },
};

export default Receipt;